import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Progress } from "./ui/progress";
import { ThemeToggle } from "./ThemeToggle";
import AnimatedCounter from "./AnimatedCounter";
import { useScrollAnimation, useTypingEffect } from "../hooks/useScrollAnimation";
import { Menu, X } from "lucide-react";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { 
  Code2, 
  User, 
  Briefcase, 
  Mail, 
  Github, 
  Linkedin, 
  ExternalLink,
  Coffee,
  Terminal,
  Cpu,
  Zap,
  Globe,
  Database,
  Smartphone,
  Phone,
  AppWindow,
  PhoneCall,
  AppWindowIcon,
  ClipboardList,
  Cloud,
  Laptop,
  LaptopMinimalCheck
} from "lucide-react";
import { mockData } from "../data/mockData";
import { useMediaQuery } from "react-responsive";

const Home = () => {
  const [activeSection, setActiveSection] = useState("index");
  const [mounted, setMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  
  const { scrollYProgress } = useScroll();
  const yRange = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const yRangeSpring = useSpring(yRange, { stiffness: 400, damping: 90 });

  // Scroll animations
  const [indexRef, indexVisible] = useScrollAnimation();
  const [aboutRef, aboutVisible] = useScrollAnimation();
  const [skillsRef, skillsVisible] = useScrollAnimation();
  const [experienceRef, experienceVisible] = useScrollAnimation();
  const [projectsRef, projectsVisible] = useScrollAnimation();
  const [contactRef, contactVisible] = useScrollAnimation();
  const [certificatesRef, certificatesVisible] = useScrollAnimation();
  const [activeTab, setActiveTab] = useState("frontend");
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains("dark"));
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      y: -10,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const floatingVariants = {
    float: {
      y: [-20, 20, -20],
      transition: {
        duration: 6,
        ease: "easeInOut",
        repeat: Infinity
      }
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 transition-colors duration-300">
        {/* Navigation Header */}
        <motion.nav 
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-gray-200 dark:border-slate-700 transition-colors duration-300"
        >
          <div style={{ width: "100%" }} className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center">
              <motion.div 
                style={{ cursor: "pointer" }}
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Terminal className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <span className="font-bold text-xl bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                  Cristian Moreno
                </span>
              </motion.div>
              
              {/* Desktop navigation */}
              <div className="hidden md:flex items-center gap-8">
                {mockData.navigator.sections.map((section, index) => (
                  <motion.button
                    key={section}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.5 }}
                    whileHover={{ y: -2, scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => scrollToSection(section)}
                    className={`text-sm font-medium transition-colors hover:text-blue-600 dark:hover:text-blue-400 ${
                      activeSection === section ? "text-blue-600 dark:text-blue-400" : "text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {mockData.navigator.items[index]}
                  </motion.button>
                ))}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  <ThemeToggle />
                </motion.div>
              </div>

              {/* Mobile navigation: hamburger button */}
              <div className="md:hidden flex items-center gap-2">
                <ThemeToggle />
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
                >
                  {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>
          {/* Mobile dropdown */}
          {menuOpen && (
            <motion.div
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -30, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden fixed top-16 left-0 right-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shadow-md z-50"
            >
              <div className="flex flex-col items-center gap-4 py-6">
                {mockData.navigator.sections.map((section, index) => (
                  <button
                    key={section}
                    onClick={() => {
                      scrollToSection(section);
                      setMenuOpen(false);
                    }}
                    className={`text-lg font-medium transition-colors hover:text-blue-600 dark:hover:text-blue-400 ${
                      activeSection === section
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {mockData.navigator.items[index]}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </motion.nav>

        {/* Hero Section */}
        <section id="index" ref={indexRef} className="pt-20 pb-16 px-6 overflow-hidden">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden"
              animate={indexVisible || mounted ? "visible" : "hidden"}
              variants={containerVariants}
              className="text-center relative"
            >
              {/* Floating background elements */}
              <motion.div 
                className="absolute -top-20 -left-20 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl"
                variants={floatingVariants}
                animate="float"
              />
              <motion.div 
                hidden={isMobile}
                className="absolute -bottom-20 -right-20 w-60 h-60 bg-purple-500/10 rounded-full blur-3xl"
                variants={floatingVariants}
                animate="float"
                transition={{ delay: 2 }}
              />

              <motion.div 
                variants={itemVariants}
                className="mb-8 relative"
              >
                <motion.div 
                  className="w-32 h-32 mx-auto mb-6 relative"
                  whileHover={{ scale: 1.1, rotate: (isMobile) ? 0 : 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 dark:from-blue-400 dark:to-purple-500 rounded-full"
                    animate={{ 
                      boxShadow: [
                        "0 0 20px rgba(59, 130, 246, 0.5)",
                        "0 0 40px rgba(147, 51, 234, 0.5)",
                        "0 0 20px rgba(59, 130, 246, 0.5)"
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                  <motion.div 
                    className="absolute inset-2 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center transition-colors duration-300"
                  >
                      <Code2 className="w-12 h-12 text-blue-600 dark:text-blue-400" />
                  </motion.div>
                </motion.div>
              </motion.div>
              
              <motion.h1 
                variants={itemVariants}
                className="text-5xl md:text-7xl font-light mb-4 text-gray-900 dark:text-white transition-colors duration-300"
              >
                Software
                <motion.span 
                  className="block font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.8, duration: 0.8, type: "spring", stiffness: 200 }}
                >
                  Developer
                </motion.span>
              </motion.h1>
              
              <motion.p 
                variants={itemVariants}
                className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto transition-colors duration-300"
              >
                Especializado en crear aplicaciones web con las tecnologías más modernas
              </motion.p>
              
              <motion.div 
                variants={itemVariants}
                className="flex flex-wrap justify-center gap-4 mb-8"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    onClick={() => scrollToSection("projects")}
                    size="lg" 
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500 dark:hover:from-blue-600 dark:hover:to-purple-600 transition-all duration-300"
                  >
                    <motion.span
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 2, duration: 0.5 }}
                    >
                      Ver Proyectos
                    </motion.span>
                    <motion.div
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </motion.div>
                  </Button>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    onClick={() => scrollToSection("contact")}
                    variant="outline" 
                    size="lg"
                    className="border-2 hover:bg-gray-50 dark:hover:bg-slate-800 dark:border-slate-600 transition-colors duration-300"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Contacto
                  </Button>
                </motion.div>
              </motion.div>

              <motion.div 
                variants={itemVariants}
                className="flex justify-center gap-6"
              >
                {[
                  { icon: Github, href: "https://github.com/cscristianmoreno" },
                  { icon: Linkedin, href: "https://www.linkedin.com/in/cristianmorenoweb/" },
                  { icon: Phone, href: "https://wa.me/+5493874450711" },
                  { icon: Mail, href: "mailto:cristianmorenoweb@gmail.com" },
                ].map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <motion.a 
                      key={index}
                      target="_blank"
                      href={social.href}
                      whileHover={{ y: -3, scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors duration-300"
                    >
                      <Icon className="h-6 w-6" />
                    </motion.a>
                  );
                })}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Projects Section */}
        <section 
          id="projects" 
          ref={projectsRef}
          className="py-16 bg-white dark:bg-slate-900 px-6 transition-colors duration-300"
        >
          <div className="max-w-6xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={projectsVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-light mb-4 text-gray-900 dark:text-white transition-colors duration-300">
                Proyectos <span className="font-bold text-blue-600 dark:text-blue-400">Destacados</span>
              </h2>
              <motion.div 
                initial={{ width: 0 }}
                animate={projectsVisible ? { width: 96 } : {}}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="h-1 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 mx-auto"
              />
            </motion.div>

            <motion.div 
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial="hidden"
              animate={projectsVisible ? "visible" : "hidden"}
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.15
                  }
                }
              }}
            >
              {mockData.projects.map((project, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <Card style={{ minHeight: 525 }} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <motion.div 
                      style={{ overflow: "hidden" }}
                      className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-400 dark:to-purple-500 flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                    >
                        {
                          (project.image === null) ?
                            <AppWindowIcon width={125} height={125} color="white"/>
                          :
                            <img 
                                src={`assets/projects/${project.image}`} 
                                alt={project.title} 
                                className="w-full h-full object-cover block"
                            />
                        }
                    </motion.div>
                    
                    <CardContent className="p-6">
                      <motion.h3 
                        className="text-xl font-semibold mb-2 text-gray-900 dark:text-white transition-colors duration-300"
                        whileHover={{ x: 5 }}
                      >
                        {project.title}
                      </motion.h3>
                      <p style={{ height: 100, overflowY: "auto" }} className="text-gray-600 dark:text-gray-400 mb-4 transition-colors duration-300">
                        {project.description}
                      </p>
                      
                      <motion.div 
                        className="flex flex-wrap gap-2 mb-4"
                        initial="hidden"
                        animate={projectsVisible ? "visible" : "hidden"}
                        variants={{
                          visible: {
                            transition: {
                              staggerChildren: 0.05
                            }
                          }
                        }}
                      >
                        {project.technologies.map((tech, techIndex) => (
                          <motion.div
                            key={techIndex}
                            variants={{
                              hidden: { scale: 0, opacity: 0 },
                              visible: { scale: 1, opacity: 1 }
                            }}
                            whileHover={{ scale: 1.1 }}
                          >
                            <Badge variant="secondary" className="dark:bg-slate-700 dark:text-slate-300">
                              {tech}
                            </Badge>
                          </motion.div>
                        ))}
                      </motion.div>
                      
                      <div className="flex gap-3">
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button disabled={(project.github === null)} onClick={(ev) => window.open(project.github, "_blank")}  variant="outline" size="sm" className="dark:border-slate-600 dark:hover:bg-slate-700">
                            <Github className="mr-2 h-4 w-4" />
                            Código
                          </Button>
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button disabled={(project.demo === null)} onClick={(ev) => window.open(project.demo, "_blank")} size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Demo
                          </Button>
                        </motion.div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Skills Section */}
        <section 
          id="skills" 
          ref={skillsRef}
          className="py-16 px-6 dark:bg-slate-900 transition-colors duration-300"
        >
          <div className="max-w-6xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={skillsVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-light mb-4 text-gray-900 dark:text-white transition-colors duration-300">
                Habilidades <span className="font-bold text-blue-600 dark:text-blue-400">Técnicas</span>
              </h2>
              <motion.div 
                initial={{ width: 0 }}
                animate={skillsVisible ? { width: 96 } : {}}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="h-1 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 mx-auto"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={skillsVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <Tabs defaultValue="frontend" className="w-full" onValueChange={setActiveTab}>
                <TabsList className={"grid grid-cols-5 w-full max-w-2xl mx-auto mb-8 bg-gray-100 dark:bg-slate-800 transition-colors duration-300" + (isMobile ? " flex flex-wrap space-around " : "")}>
                  {["frontend", "backend", "database", "cloud", "cicd"].map((tab, index) => (
                    <motion.div
                      key={tab}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <TabsTrigger 
                        value={tab} 
                        className="dark:data-[state=active]:bg-slate-700 capitalize"
                      >
                        {tab === "cicd" ? "CI / CD" : tab}
                      </TabsTrigger>
                    </motion.div>
                  ))}
                </TabsList>

                <div className="col-span-full">
                  <SyntaxHighlighter
                    language={mockData.skills.codeExamples[activeTab].language}
                    style={isDark ? vscDarkPlus : prism}
                    customStyle={{
                      borderRadius: "0.5rem",
                      fontSize: "14px",
                      margin: 0,
                      background: "transparent",
                      maxHeight: 400
                    }}
                    showLineNumbers
                  >
                    {mockData.skills.codeExamples[activeTab].code}
                  </SyntaxHighlighter>
                  <div className="text-xs text-right text-gray-400 mt-1">
                    {mockData.skills.codeExamples[activeTab].language.toUpperCase()}
                  </div>
                </div>

                {Object.entries(mockData.skills)
                .filter(([category]) => category !== "codeExamples")
                .map(([category, skills]) => (
                  <TabsContent key={category} value={category}>
                    <motion.div 
                      className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
                      initial="hidden"
                      animate={skillsVisible ? "visible" : "hidden"}
                      variants={{
                        visible: {
                          transition: {
                            staggerChildren: 0.1
                          }
                        }
                      }}
                    >
                      {skills.map((skill, index) => (
                        <motion.div
                          key={index}
                          variants={cardVariants}
                          whileHover="hover"
                        >
                          <Card className="hover:shadow-lg transition-all duration-300 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                            <CardContent className="p-6">
                              <div className="flex items-center gap-3 mb-4">
                                <motion.div 
                                  className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg transition-colors duration-300"
                                  whileHover={{ scale: 1.1 }}
                                  transition={{ duration: 0.5 }}
                                >
                                  {category === "frontend" && <LaptopMinimalCheck className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
                                  {category === "backend" && <Code2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
                                  {category === "database" && <Database className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
                                  {category === "cloud" && <Cloud className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
                                  {category === "cicd" && <Code2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
                                </motion.div>
                                <div>
                                  <h3 className="font-semibold text-gray-900 dark:text-white transition-colors duration-300">{skill.name}</h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
                                    {/* {skillsVisible && <AnimatedCounter value={skill.level} />} */}
                                  </p>
                                </div>
                              </div>
                              {/* <Progress value={skill.level} className="h-2" /> */}
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </motion.div>
                  </TabsContent>
                ))}
              </Tabs>
            </motion.div>
          </div>
        </section>

        {/* Experience Section */}
        <section 
          id="experience" 
          ref={experienceRef}
          className="py-16 bg-white dark:bg-slate-900 px-6 transition-colors duration-300"
        >
          <div className="max-w-6xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={experienceVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-light mb-4 text-gray-900 dark:text-white transition-colors duration-300">
                Experiencia <span className="font-bold text-blue-600 dark:text-blue-400">Laboral</span>
              </h2>
              <motion.div 
                initial={{ width: 0 }}
                animate={experienceVisible ? { width: 96 } : {}}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="h-1 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 mx-auto"
              />
            </motion.div>

            <motion.div 
              className="space-y-8"
              initial="hidden"
              animate={experienceVisible ? "visible" : "hidden"}
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.2
                  }
                }
              }}
            >
              {mockData.experience.map((exp, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <Card className="hover:shadow-lg transition-all duration-300 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <motion.div 
                          className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg transition-colors duration-300"
                          whileHover={{ scale: 1.1, rotate: (isMobile) ? 0 : 5 }}
                        >
                          <Briefcase className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </motion.div>
                        
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-2">
                            <motion.h3 
                              className="text-xl font-semibold text-gray-900 dark:text-white transition-colors duration-300"
                              whileHover={{ x: 5 }}
                            >
                              {exp.position}
                            </motion.h3>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                            >
                              <Badge variant="secondary" className="dark:bg-slate-700 dark:text-slate-300">{exp.period}</Badge>
                            </motion.div>
                          </div>
                          
                          <h4 className="text-lg text-blue-600 dark:text-blue-400 mb-3 transition-colors duration-300">{exp.company}</h4>
                          <p className="text-gray-700 dark:text-gray-300 mb-4 transition-colors duration-300">{exp.description}</p>
                          
                          <motion.div 
                            className="flex flex-wrap gap-2"
                            initial="hidden"
                            animate={experienceVisible ? "visible" : "hidden"}
                            variants={{
                              visible: {
                                transition: {
                                  staggerChildren: 0.05
                                }
                              }
                            }}
                          >
                            {exp.technologies.map((tech, techIndex) => (
                              <motion.div
                                key={techIndex}
                                variants={{
                                  hidden: { scale: 0, opacity: 0 },
                                  visible: { scale: 1, opacity: 1 }
                                }}
                                whileHover={{ scale: 1.1 }}
                              >
                                <Badge variant="outline" className="dark:border-slate-600 dark:text-slate-300">
                                  {tech}
                                </Badge>
                              </motion.div>
                            ))}
                          </motion.div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* About Section */}
        <section 
          id="about" 
          ref={aboutRef}
          className="py-16 px-6 dark:bg-slate-900 transition-colors duration-300"
        >
          <div className="max-w-6xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={aboutVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-light mb-4 text-gray-900 dark:text-white transition-colors duration-300">
                Acerca de <span className="font-bold text-blue-600 dark:text-blue-400">Mí</span>
              </h2>
              <motion.div 
                initial={{ width: 0 }}
                animate={aboutVisible ? { width: 96 } : {}}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="h-1 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 mx-auto"
              />
            </motion.div>

            <div  className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: (isMobile) ? 0 : -100 }}
                animate={aboutVisible ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                <motion.div 
                  className="w-80 h-80 mx-auto bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center transition-colors duration-300"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <motion.div 
                    className="w-64 h-64 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-400 dark:to-purple-500 rounded-xl flex items-center justify-center"
                    whileHover={{ scale: 1.1 }}
                  >
                    {/* <User className="w-32 h-32 text-white" /> */}
                    <img src="assets/profile.jpg"/>
                  </motion.div>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: (isMobile) ? 0 : 100 }}
                animate={aboutVisible ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                <motion.p 
                  className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed transition-colors duration-300"
                  initial={{ opacity: 0 }}
                  animate={aboutVisible ? { opacity: 1 } : {}}
                  transition={{ delay: 0.8, duration: 0.8 }}
                >
                  {mockData.about.description}
                </motion.p>
                
                <div className="space-y-4">
                  <motion.h3 
                    className="text-xl font-semibold text-gray-900 dark:text-white mb-4 transition-colors duration-300"
                    initial={{ opacity: 0 }}
                    animate={aboutVisible ? { opacity: 1 } : {}}
                    transition={{ delay: 1, duration: 0.8 }}
                  >
                    ¿Por qué elegirme?
                  </motion.h3>
                  {mockData.about.highlights.map((highlight, index) => (
                    <motion.div 
                      key={index} 
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={aboutVisible ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 1.2 + index * 0.1, duration: 0.6 }}
                      whileHover={{ x: 5 }}
                    >
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                      >
                        <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-1" />
                      </motion.div>
                      <p className="text-gray-700 dark:text-gray-300 transition-colors duration-300">{highlight}</p>
                    </motion.div>
                  ))}
                  <Button
                    onClick={() => window.open("https://drive.google.com/uc?export=download&id=1BBEL9xgJsquGd7HNGmqvHdOMCiSxtw6m")}
                    size="lg" 
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500 dark:hover:from-blue-600 dark:hover:to-purple-600 transition-all duration-300"
                  >
                      Descargar CV
                      <ClipboardList className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        {/* Certificates Section */}
        <section
          id="certificates"
          ref={certificatesRef}
          className="py-16 bg-white dark:bg-slate-900 px-6 transition-colors duration-300"
        >
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={certificatesVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-light mb-4 text-gray-900 dark:text-white transition-colors duration-300">
                Certificados <span className="font-bold text-blue-600 dark:text-blue-400">Obtenidos</span>
              </h2>
              <motion.div
                initial={{ width: 0 }}
                animate={certificatesVisible ? { width: 96 } : {}}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="h-1 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 mx-auto"
              />
            </motion.div>

            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial="hidden"
              animate={certificatesVisible ? "visible" : "hidden"}
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.15
                  }
                }
              }}
            >
              {mockData.certificates.map((cert, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <Card style={{ minHeight: 420 }} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <motion.div
                      style={{ overflow: "hidden" }}
                      className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-400 dark:to-purple-500 flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                    >
                      <img
                        src={"/assets/certificates/" + cert.image}
                        alt={cert.title}
                        className="w-full h-full object-cover block"
                        loading="lazy"
                      />
                    </motion.div>
                    <CardContent className="p-6 flex flex-col">
                      <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white font-experience">
                        {cert.title}
                      </h3>
                      <span className="text-blue-600 dark:text-blue-400 text-sm font-experience mb-1">{cert.issuer}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-xs font-experience mb-3">{cert.date}</span>
                      <p className="text-gray-600 dark:text-gray-400 mb-4 transition-colors duration-300" style={{ minHeight: 48 }}>
                        {cert.description}
                      </p>
                      <div className="flex gap-3 mt-auto">
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button
                            asChild
                            size="sm"
                            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500"
                          >
                            <a
                              href={cert.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center"
                            >
                              Ver certificado
                              <ExternalLink className="ml-2 h-4 w-4" />
                            </a>
                          </Button>
                        </motion.div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section 
          id="contact" 
          ref={contactRef}
          className="py-16 px-6 dark:bg-slate-900 transition-colors duration-300"
        >
          <div className="max-w-4xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={contactVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="mb-12"

            >
              <h2 className="text-3xl md:text-4xl font-light mb-4 text-gray-900 dark:text-white transition-colors duration-300">
                ¿Listo para <span className="font-bold text-blue-600 dark:text-blue-400">Colaborar?</span>
              </h2>
              <motion.div 
                initial={{ width: 0 }}
                animate={contactVisible ? { width: 96 } : {}}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="h-1 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 mx-auto mb-6"
              />
              <motion.p 
                initial={{ opacity: 0 }}
                animate={contactVisible ? { opacity: 1 } : {}}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="text-xl text-gray-600 dark:text-gray-300 transition-colors duration-300"
              >
                Hagamos tu próxima idea realidad
              </motion.p>
            </motion.div>

            <motion.div 
              className="grid md:grid-cols-3 gap-8 mb-12"
              initial="hidden"
              animate={contactVisible ? "visible" : "hidden"}
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.15
                  }
                }
              }}
            >
              {mockData.contact.methods.map((method, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <Card style={{ cursor: "pointer" }} className="hover:shadow-lg transition-all duration-300 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <CardContent className="p-6 text-center">
                      <motion.div 
                        className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full inline-block mb-4 transition-colors duration-300"
                        transition={{ duration: 0.5 }}
                      >
                        {method.icon}
                      </motion.div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">
                        {method.type}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 transition-colors duration-300">{method.value}</p>
                      {/* Añade aquí el botón */}
                      <div className="flex justify-center mt-4">
                        <Button
                          asChild
                          size="sm"
                          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500"
                        >
                          <a
                            href={method.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center"
                          >
                            Contactar
                            <ExternalLink className="ml-2 h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>

            
          </div>
        </section>

        {/* Footer */}
        <motion.footer 
          className="py-8 px-6 bg-white bg-gray-50 dark:bg-slate-800 transition-colors duration-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          <div className="max-w-6xl mx-auto text-center">
            <motion.p 
              className="text-gray-600 dark:text-gray-400 transition-colors duration-300"
              whileHover={{ scale: 1.02 }}
            >
              © 2024 Cristian Moreno. Diseño y desarrollo.
            </motion.p>
          </div>
        </motion.footer>
      </div>
    </>
  );
};

export default Home;